import {atom} from 'jotai';
import {atomWithStorage} from 'jotai/utils';
import {CHECKOUT} from '@/lib/constants';
import {
    BillingCartAddress,
    Cart,
    ShippingCartAddress,
    Maybe,
    AppliedCoupon,
    AvailablePaymentMethod,
    DiscountKicoins,
    SelectedPaymentMethod,
} from '@/__generated__/__types__';

export interface CheckoutState extends Cart {
    increment_id?: string; // Order number
}

export const defaultCheckout: CheckoutState = {
    __typename: 'Cart',
    applied_coupon: undefined,
    applied_coupons: undefined,
    available_payment_methods: undefined,
    billing_address: undefined,
    discount_kicoins: undefined,
    email: undefined,
    id: '',
    is_virtual: false,
    items: undefined,
    prices: undefined,
    selected_payment_method: undefined,
    shipping_addresses: [],
    total_quantity: 0,
};

// Original storage atom
export const checkoutAtom = atomWithStorage(CHECKOUT, defaultCheckout);

// Clear the entire checkout state
export const clearCheckoutAtom = atom(null, (_get, set) => {
    return set(checkoutAtom, defaultCheckout);
});

// Individual atoms for each checkout property
export const appliedCouponAtom = atom(
    get => get(checkoutAtom).applied_coupon,
    (get, set, data: Maybe<AppliedCoupon>) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, applied_coupon: data});
    },
);

export const appliedCouponsAtom = atom(
    get => get(checkoutAtom).applied_coupons,
    (get, set, data: Maybe<Array<Maybe<AppliedCoupon>>>) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, applied_coupons: data});
    },
);

export const availablePaymentMethodsAtom = atom(
    get => get(checkoutAtom).available_payment_methods,
    (get, set, data: Maybe<Array<Maybe<AvailablePaymentMethod>>>) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, available_payment_methods: data});
    },
);

export const billingAddressAtom = atom(
    get => get(checkoutAtom).billing_address,
    (get, set, data: BillingCartAddress | undefined) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, billing_address: data});
    },
);

export const discountKicoinsAtom = atom(
    get => get(checkoutAtom).discount_kicoins,
    (get, set, data: Maybe<DiscountKicoins>) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, discount_kicoins: data});
    },
);

export const emailAtom = atom(
    get => get(checkoutAtom).email,
    (get, set, data: string | undefined) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, email: data});
    },
);

export const idAtom = atom(
    get => get(checkoutAtom).id,
    (get, set, data: string) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, id: data});
    },
);

export const isVirtualAtom = atom(
    get => get(checkoutAtom).is_virtual,
    (get, set, data: boolean) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, is_virtual: data});
    },
);

export const itemsAtom = atom(
    get => get(checkoutAtom).items,
    (get, set, data: Cart['items'] | undefined) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, items: data});
    },
);

export const pricesAtom = atom(
    get => get(checkoutAtom).prices,
    (get, set, data: Cart['prices'] | undefined) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, prices: data});
    },
);

export const selectedPaymentMethodAtom = atom(
    get => get(checkoutAtom).selected_payment_method,
    (get, set, data: Maybe<SelectedPaymentMethod>) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, selected_payment_method: data});
    },
);

export const shippingAddressesAtom = atom(
    get => get(checkoutAtom).shipping_addresses,
    (get, set, data: Array<Maybe<ShippingCartAddress>>) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, shipping_addresses: data});
    },
);

export const totalQuantityAtom = atom(
    get => get(checkoutAtom).total_quantity,
    (get, set, data: number) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, total_quantity: data});
    },
);

export const incrementIdAtom = atom(
    get => get(checkoutAtom).increment_id,
    (get, set, data: string) => {
        const prev = get(checkoutAtom);
        return set(checkoutAtom, {...prev, increment_id: data});
    },
);

export const orderNumberAfterPlaceAtom = atom<string>('');

export const isPollingPaymentStatusAtom = atom<boolean>(false);

export const checkoutLoadingBlockCheckAddressAtom = atom<boolean>(false);
